import { log } from '../utils/DebugLogger';
import { eventBus } from '../eventBus';
import { permissionsService } from '../services/permissionsService';

/**
 * CEP lookup, franchise change, and document config logic for ClienteForm.
 * Extracted from useClienteForm to keep file sizes manageable.
 *
 * @param {Object} deps
 * @param {Function} deps.t - i18n translate function
 * @param {import('vue').Ref} deps.formData - reactive form data
 * @param {import('vue').Ref} deps.phoneModel - reactive phone model
 * @param {import('vue').Ref} deps.locationSelectReady - location select ready flag
 * @param {import('vue').ComputedRef} deps.isBrazilianCountry - is Brazil computed
 * @param {Object} deps.countryConfig - country configuration ref
 * @param {Object} deps.intlConfig - { setCountryFromName, setDocumentConfig, selectedDocumentType, setSelectedDocumentType }
 * @param {Object} deps.clienteApi - { consultaCep, fetchDocumentConfig }
 */
export function useClienteFormAddress(deps: any) {
  const {
    t, formData, phoneModel, locationSelectReady,
    isBrazilianCountry, countryConfig, intlConfig, clienteApi
  } = deps;

  // Race condition guard for doc config requests
  let latestDocConfigFranchiseId = null;

  /**
   * Handle CEP input: lookup address when 8 digits entered (Brazil only).
   */
  async function handleCepInput() {
    const cleanCep = formData.value.cliente_cep.replace(/\D/g, '');

    if (cleanCep.length !== 8 || !isBrazilianCountry.value) {
      return;
    }

    log('ClienteForm', 'handleCepInput', 'Looking up CEP', formData.value.cliente_cep);

    try {
      const dados = await clienteApi.consultaCep(formData.value.cliente_cep);
      log('ClienteForm', 'handleCepInput', 'CEP API returned data:', dados);

      formData.value.cliente_rua = dados.logradouro || '';
      formData.value.cliente_bairro = dados.bairro || '';

      matchLocationFromCep(dados);

      log('ClienteForm', 'handleCepInput', 'CEP lookup successful');
    } catch (error) {
      log('ClienteForm', 'handleCepInput', 'Error looking up CEP', error);
      eventBus.emit('globalMessage.error', t('Erro ao consultar CEP'));
    }
  }

  /**
   * Try to match a location from CEP data (city then neighborhood).
   */
  function matchLocationFromCep(dados: any) {
    if (!dados.localidade || !formData.value.cliente_franquia_id) {
      return;
    }

    const cityName = dados.localidade.toLowerCase().trim();
    const locations = permissionsService.getLocationsByID(formData.value.cliente_franquia_id);

    log('ClienteForm', 'handleCepInput', 'Searching for city:', cityName);

    const finalMatch = findCityMatch(locations, cityName)
      || findNeighborhoodMatch(locations, cityName, dados.bairro);

    if (!finalMatch) {
      log('ClienteForm', 'handleCepInput', 'No matching location found for city:', cityName);
      return;
    }

    log('ClienteForm', 'handleCepInput', 'Location matched:', finalMatch);
    formData.value.cliente_location_id = finalMatch.ID;
    formData.value.cliente_location_type = finalMatch.Type;
    eventBus.emit('globalMessage.success', t('Localidade selecionada automaticamente: ') + finalMatch.Name);
  }

  /**
   * Find a city-type location matching the given city name.
   */
  function findCityMatch(locations: any[], cityName: string) {
    return locations.find(loc =>
      loc.Type === 'city' && loc.Name && loc.Name.toLowerCase().trim() === cityName
    ) || null;
  }

  /**
   * Find a neighborhood-type location matching city + bairro.
   */
  function findNeighborhoodMatch(locations: any[], cityName: string, bairro: string) {
    if (!bairro) {
      return null;
    }
    return locations.find(loc =>
      loc.Type === 'neighborhood' &&
      loc.City && loc.City.toLowerCase().trim() === cityName &&
      loc.Name && loc.Name.toLowerCase().includes(bairro.toLowerCase())
    ) || null;
  }

  /**
   * Handle franchise data event (sets country config + phone DDI).
   */
  function handleFranquiaData(franquiaData: any) {
    log('ClienteForm', 'handleFranquiaData', 'Received franchise data:', franquiaData);

    if (!franquiaData?.Dados?.Pais) {
      return;
    }

    intlConfig.setCountryFromName(franquiaData.Dados.Pais);
    log('ClienteForm', 'handleFranquiaData', 'Country set to:', franquiaData.Dados.Pais);
    eventBus.emit('cliente.form.countryChanged', { country: franquiaData.Dados.Pais });

    if (phoneModel.value) {
      phoneModel.value.countryCode = countryConfig.value.phoneDdi;
      log('ClienteForm', 'handleFranquiaData', 'Phone DDI updated to:', countryConfig.value.phoneDdi);
    }
  }

  /**
   * Handle franchise change: fetch doc config and clear location if user-initiated.
   */
  async function handleFranquiaChange(franchise_id: any) {
    if (franchise_id) {
      await fetchAndApplyDocConfig(franchise_id);
    }

    if (!locationSelectReady.value) {
      return;
    }
    if (franchise_id == formData.value.cliente_franquia_id) {
      return;
    }

    log('ClienteForm', 'handleFranquiaChange', 'User-initiated franchise change - clearing location');
    formData.value.cliente_location_id = '';
    formData.value.cliente_location_type = '';
  }

  /**
   * Fetch document config for a franchise and apply it (with race condition guard).
   */
  async function fetchAndApplyDocConfig(franchise_id: any) {
    latestDocConfigFranchiseId = String(franchise_id);

    const docConfig = await clienteApi.fetchDocumentConfig(franchise_id).catch((err: any) => {
      log('ClienteForm', 'handleFranquiaChange', 'Error fetching document config', err);
      return null;
    });

    if (!docConfig) {
      return;
    }
    if (String(franchise_id) !== latestDocConfigFranchiseId) {
      log('ClienteForm', 'handleFranquiaChange', 'Stale response ignored', {
        fetched: franchise_id, latest: latestDocConfigFranchiseId
      });
      return;
    }

    intlConfig.setDocumentConfig(docConfig);
    preserveOrDefaultDocumentType(docConfig);
    formData.value.cliente_document_type = intlConfig.selectedDocumentType.value;
    log('ClienteForm', 'handleFranquiaChange', 'Document config loaded:', docConfig);
  }

  /**
   * Preserve saved document type if still valid, otherwise use the default.
   */
  function preserveOrDefaultDocumentType(docConfig: any) {
    const savedType = formData.value.cliente_document_type;
    const isValid = savedType && docConfig.document_types && docConfig.document_types.includes(savedType);
    if (isValid) {
      intlConfig.setSelectedDocumentType(savedType);
    }
  }

  /**
   * Handle document type change from selector.
   */
  function handleDocumentTypeChange(type: string) {
    intlConfig.setSelectedDocumentType(type);
    formData.value.cliente_document_type = type;
    formData.value.cliente_cpf = '';
    log('ClienteForm', 'handleDocumentTypeChange', 'Document type changed to:', type);
  }

  /**
   * Handle document validation result from InputDocument.
   */
  function handleDocumentValidation(result: any) {
    log('ClienteForm', 'handleDocumentValidation', 'Validation result:', result);
    return result.valid ? '' : result.error;
  }

  return {
    handleCepInput,
    handleFranquiaData,
    handleFranquiaChange,
    handleDocumentTypeChange,
    handleDocumentValidation,
  };
}
